import { supabase } from './supabase';
import { parseMenuText, importSlug, guessCategoryEmoji, type ImportedMenu } from './menuImport';

/**
 * Service d'IMPORT de menu (Brique 3, phase B) — pose en base.
 * S'appuie sur le parseur pur (menuImport.ts) et sur la fonction RPC sécurisée
 * `import_menu` (cf. sql/19_import_menu_rpc.sql) qui remplace le menu du tenant
 * en une transaction, réservée au propriétaire du resto.
 */

export interface ImportResult {
  ok: boolean;
  /** nombre de produits importés (si succès). */
  count?: number;
  /** message d'erreur prêt à afficher (si échec). */
  error?: string;
}

/**
 * Rend un slug UNIQUE dans un ensemble déjà vu (suffixe -2, -3…), et l'y ajoute.
 * Les tables `categories` et `products` ont chacune une contrainte UNIQUE (tenant_id, slug) :
 * sans ça, deux produits de même nom (ex. « Coca-Cola » dans deux catégories, un plat
 * répété par la lecture IA) donnent le même slug → l'INSERT casse et TOUT l'import est
 * annulé (« duplicate key value violates unique constraint »). On garantit l'unicité ici.
 */
export function dedupeSlug(base: string, used: Set<string>): string {
  const root = base || 'item';
  let slug = root;
  let n = 2;
  while (used.has(slug)) slug = `${root}-${n++}`;
  used.add(slug);
  return slug;
}

/**
 * Rend un NOM de produit unique dans un ensemble déjà vu (suffixe « (2) », « (3) »…).
 * La base a AUSSI une contrainte UNIQUE (tenant_id, name) (`products_tenant_name_uniq`,
 * posée directement en base, hors migrations) : deux produits de même nom à prix
 * DIFFÉRENT (ex. « Coca-Cola » en Boissons ET en Menu enfant) cassent l'INSERT sinon.
 * Comparaison insensible à la casse pour éviter les quasi-doublons. Renvoie le nom final.
 */
export function dedupeName(base: string, used: Set<string>): string {
  const root = (base || 'Produit').trim();
  let name = root;
  let n = 2;
  while (used.has(name.toLowerCase())) name = `${root} (${n++})`;
  used.add(name.toLowerCase());
  return name;
}

/**
 * Convertit le menu parsé en charge JSON attendue par la RPC, en respectant les DEUX
 * contraintes UNIQUE de la table products : (tenant_id, slug) ET (tenant_id, name).
 *  - doublon EXACT (même nom + même prix, typiquement lu 2× par l'IA) → ignoré ;
 *  - même nom à prix différent → nom suffixé « (2) », « (3) »… ;
 *  - slug recalculé depuis le nom final, puis dédoublonné à son tour.
 */
export function toRpcPayload(menu: ImportedMenu): unknown[] {
  const catSlugs = new Set<string>();
  const prodSlugs = new Set<string>();
  const prodNames = new Set<string>();       // noms déjà pris (insensible à la casse)
  const seenExact = new Set<string>();        // « nom|prix » déjà vu → doublon exact
  return menu.categories.map((c) => ({
    name: c.name,
    slug: dedupeSlug(importSlug(c.name), catSlugs),
    emoji: c.emoji,
    products: c.products.flatMap((p) => {
      const raw = (p.name || 'Produit').trim();
      const exactKey = `${raw.toLowerCase()}|${p.priceCents}`;
      if (seenExact.has(exactKey)) return []; // doublon exact → on ignore
      seenExact.add(exactKey);
      const name = dedupeName(raw, prodNames);              // unicité du NOM
      const slug = dedupeSlug(importSlug(name), prodSlugs); // unicité du SLUG
      return [{ name, slug, description: p.description, price_cents: p.priceCents }];
    }),
  }));
}

/**
 * Importe un menu collé (texte Uber Eats, etc.) pour un tenant :
 * 1) parse le texte, 2) refuse si rien d'exploitable, 3) appelle la RPC sécurisée.
 */
export async function importMenuFromText(
  tenantId: string,
  text: string,
): Promise<ImportResult> {
  const menu = parseMenuText(text);
  if (menu.totals.products === 0) {
    return { ok: false, error: "Aucun produit détecté. Vérifiez le texte collé (noms + prix)." };
  }

  const { data, error } = await supabase.rpc('import_menu', {
    p_tenant_id: tenantId,
    p_menu: toRpcPayload(menu),
  });
  if (error) {
    return { ok: false, error: `Import impossible : ${error.message}` };
  }

  // Filet emoji (best-effort) : garantit un emoji adapté sur chaque catégorie,
  // même si la RPC d'import ne persiste pas le champ `emoji`.
  try {
    await syncCategoryEmojis(tenantId);
  } catch {
    // non bloquant
  }

  return { ok: true, count: typeof data === 'number' ? data : menu.totals.products };
}

/**
 * Renseigne un emoji adapté sur les catégories du tenant qui n'en ont pas
 * (null/vide) ou qui portent l'emoji générique « 🍴 ». Idempotent, best-effort.
 * Renvoie le nombre de catégories mises à jour. Permet aussi de corriger des
 * catégories importées AVANT l'ajout de l'emoji auto.
 */
export async function syncCategoryEmojis(tenantId: string): Promise<number> {
  const { data } = await supabase
    .from('categories')
    .select('id, name, emoji')
    .eq('tenant_id', tenantId);
  const cats = (data as { id: string; name: string; emoji: string | null }[] | null) ?? [];
  let updated = 0;
  for (const c of cats) {
    const current = (c.emoji ?? '').trim();
    if (current !== '' && current !== '🍴') continue; // emoji déjà choisi → on respecte
    const guessed = guessCategoryEmoji(c.name);
    const { error } = await supabase.from('categories').update({ emoji: guessed }).eq('id', c.id);
    if (!error) updated += 1;
  }
  return updated;
}
